import React, { useState, useEffect } from 'react';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiUserCheck, FiPhone, FiMail, FiFileText } from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from 'axios';
import styles from './PacientesNew.module.css';
import LoadingSpinner from '../components/LoadingSpinner';

const Medicos = () => {
  const [medicos, setMedicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMedico, setEditingMedico] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    crm: '',
    uf_crm: '',
    especialidade: '',
    telefone: '',
    email: '',
    endereco: '',
    cep: '',
    cidade: '',
    estado: '',
    status: 'ativo'
  });

  useEffect(() => {
    fetchMedicos();
  }, []);

  const fetchMedicos = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/medical/medicos');
      if (response.data.success) {
        setMedicos(response.data.data.medicos || []);
      }
    } catch (error) {
      toast.error('Erro ao carregar médicos');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMedico) {
        await axios.put(`/medical/medicos/${editingMedico.id}`, formData);
        toast.success('Médico atualizado com sucesso!');
      } else {
        await axios.post('/medical/medicos', formData);
        toast.success('Médico cadastrado com sucesso!');
      }
      
      resetForm();
      fetchMedicos();
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao salvar médico';
      toast.error(message);
    }
  };

  const handleEdit = (medico) => {
    setEditingMedico(medico);
    setFormData({
      nome: medico.nome || medico.nome_completo || '',
      crm: medico.crm || '',
      uf_crm: medico.uf_crm || '',
      especialidade: medico.especialidade || '',
      telefone: medico.telefone || '',
      email: medico.email || '',
      endereco: medico.endereco || '',
      cep: medico.cep || '',
      cidade: medico.cidade || '',
      estado: medico.estado || '',
      status: medico.status || 'ativo'
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este médico?')) {
      try {
        await axios.delete(`/medical/medicos/${id}`);
        toast.success('Médico excluído com sucesso!');
        fetchMedicos();
      } catch (error) {
        const message = error.response?.data?.message || 'Erro ao excluir médico';
        toast.error(message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      crm: '',
      especialidade: '',
      telefone: '',
      email: '',
      endereco: '',
      cep: '',
      cidade: '',
      estado: '',
      status: 'ativo'
    });
    setEditingMedico(null);
    setShowForm(false);
  };

  const filteredMedicos = medicos.filter(medico =>
    medico.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medico.crm?.includes(searchTerm) ||
    medico.especialidade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medico.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Gestão de Médicos</h1>
        <p>Gerencie o cadastro e informações dos médicos</p>
      </div>

      <div className={styles.actions}>
        <div className="formInputWrapper" style={{ flex: 1, maxWidth: '400px' }}>
          <FiSearch className="formInputIcon" />
          <input
            type="text"
            placeholder="Buscar por nome, CRM, especialidade ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="formInput"
          />
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="formButton primary"
        >
          <FiPlus /> Novo Médico
        </button>
      </div>

      {showForm && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>{editingMedico ? 'Editar Médico' : 'Novo Médico'}</h2>
              <button onClick={resetForm} className={styles.closeButton}>×</button>
            </div>
            
            <div className={styles.modalBody}>
              <form onSubmit={handleSubmit} className="form">
              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel">Nome Completo</label>
                    <div className="formInputWrapper">
                      <FiUserCheck className="formInputIcon" />
                      <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleInputChange}
                        required
                        placeholder="Nome completo do médico"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel">CRM</label>
                    <div className="formInputWrapper">
                      <FiFileText className="formInputIcon" />
                      <input
                        type="text"
                        name="crm"
                        value={formData.crm}
                        onChange={handleInputChange}
                        required
                        placeholder="CRM do médico"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel">UF do CRM</label>
                    <div className="formInputWrapper">
                      <FiFileText className="formInputIcon" />
                      <select
                        name="uf_crm"
                        value={formData.uf_crm}
                        onChange={handleInputChange}
                        required
                        className="formSelect"
                      >
                        <option value="">Selecione a UF</option>
                        <option value="AC">AC - Acre</option>
                        <option value="AL">AL - Alagoas</option>
                        <option value="AP">AP - Amapá</option>
                        <option value="AM">AM - Amazonas</option>
                        <option value="BA">BA - Bahia</option>
                        <option value="CE">CE - Ceará</option>
                        <option value="DF">DF - Distrito Federal</option>
                        <option value="ES">ES - Espírito Santo</option>
                        <option value="GO">GO - Goiás</option>
                        <option value="MA">MA - Maranhão</option>
                        <option value="MT">MT - Mato Grosso</option>
                        <option value="MS">MS - Mato Grosso do Sul</option>
                        <option value="MG">MG - Minas Gerais</option>
                        <option value="PA">PA - Pará</option>
                        <option value="PB">PB - Paraíba</option>
                        <option value="PR">PR - Paraná</option>
                        <option value="PE">PE - Pernambuco</option>
                        <option value="PI">PI - Piauí</option>
                        <option value="RJ">RJ - Rio de Janeiro</option>
                        <option value="RN">RN - Rio Grande do Norte</option>
                        <option value="RS">RS - Rio Grande do Sul</option>
                        <option value="RO">RO - Rondônia</option>
                        <option value="RR">RR - Roraima</option>
                        <option value="SC">SC - Santa Catarina</option>
                        <option value="SP">SP - São Paulo</option>
                        <option value="SE">SE - Sergipe</option>
                        <option value="TO">TO - Tocantins</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel">Especialidade</label>
                    <div className="formInputWrapper">
                      <FiUserCheck className="formInputIcon" />
                      <select
                        name="especialidade"
                        value={formData.especialidade}
                        onChange={handleInputChange}
                        required
                        className="formSelect"
                      >
                        <option value="">Selecione uma especialidade</option>
                        <option value="cardiologia">Cardiologia</option>
                        <option value="dermatologia">Dermatologia</option>
                        <option value="neurologia">Neurologia</option>
                        <option value="ortopedia">Ortopedia</option>
                        <option value="pediatria">Pediatria</option>
                        <option value="psiquiatria">Psiquiatria</option>
                        <option value="clinica_geral">Clínica Geral</option>
                        <option value="ginecologia">Ginecologia</option>
                        <option value="oftalmologia">Oftalmologia</option>
                        <option value="otorrinolaringologia">Otorrinolaringologia</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Status</label>
                    <div className="formInputWrapper">
                      <FiUserCheck className="formInputIcon" />
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="formSelect"
                      >
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Telefone</label>
                    <div className="formInputWrapper">
                      <FiPhone className="formInputIcon" />
                      <input
                        type="tel"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleInputChange}
                        placeholder="(00) 00000-0000"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Email</label>
                    <div className="formInputWrapper">
                      <FiMail className="formInputIcon" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="email@exemplo.com"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="formActions">
                <button type="button" onClick={resetForm} className="formButton formButtonSecondary">
                  Cancelar
                </button>
                <button type="submit" className="formButton formButtonPrimary">
                  {editingMedico ? 'Atualizar' : 'Cadastrar'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      <div className={styles.tableContainer}>
        {filteredMedicos.length > 0 ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Médico</th>
                <th>CRM</th>
                <th>Especialidade</th>
                <th>Contato</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredMedicos.map((medico) => (
                <tr key={medico.id}>
                  <td>
                    <div className={styles.userInfo}>
                      <div className={styles.avatar}>
                        <FiUserCheck />
                      </div>
                      <div>
                        <div className={styles.name}>{medico.nome}</div>
                      </div>
                    </div>
                  </td>
                  <td>{medico.crm}</td>
                  <td>
                    <span className={styles.specialty}>
                      {medico.especialidade}
                    </span>
                  </td>
                  <td>
                    <div className={styles.contact}>
                      {medico.telefone && <div><FiPhone /> {medico.telefone}</div>}
                      {medico.email && <div><FiMail /> {medico.email}</div>}
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.status} ${styles[medico.status]}`}>
                      {medico.status === 'ativo' ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        onClick={() => handleEdit(medico)}
                        className={styles.editButton}
                        title="Editar"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        onClick={() => handleDelete(medico.id)}
                        className={styles.deleteButton}
                        title="Excluir"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className={styles.emptyState}>
            <FiUserCheck className={styles.emptyIcon} />
            <h3>Nenhum médico encontrado</h3>
            <p>Comece cadastrando seu primeiro médico</p>
            <button
              onClick={() => setShowForm(true)}
              className={styles.addButton}
            >
              <FiPlus /> Cadastrar Médico
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Medicos;